import React from 'react';
import { View, Text, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from './Unicon';

export default function Chip({ children, colorVariant = 'textSecondary', icon, iconLibrary = 'ionicon', iconSize = 12, iconStyle, solid, style, textStyle, onPress = null, onRemove = null, removeTestID, testID, selected }) {
    const renderLabel = content => {
        const items = React.Children.toArray(content);
        if (items.every(item => typeof item === 'string' || typeof item === 'number')) {
            return <Text numberOfLines={1} style={[styles.chipText(colorVariant), textStyle]}>{content}</Text>;
        }
        return React.Children.map(content, item => {
            if (typeof item === 'string' || typeof item === 'number') return renderLabel(item);
            if (React.isValidElement(item) && item.type === React.Fragment) return renderLabel(item.props.children);
            return item;
        });
    };
    const chipContent = (
        <>
            {icon && <Unicon name={icon} library={iconLibrary} size={iconSize} colorVariant={colorVariant} iconStyle={iconStyle} solid={solid} />}
            {renderLabel(children)}
            {onRemove && (
                <Pressable
                    testID={removeTestID}
                    hitSlop={6}
                    // stopPropagation on pointerdown AND press: on web the chip can sit
                    // inside a Radix/Zeego dropdown trigger, which opens on pointerdown.
                    // On native this Pressable is deeper than any trigger Pressable,
                    // so it wins the responder and the trigger never fires.
                    onPointerDown={(e) => { e?.stopPropagation?.(); e?.preventDefault?.(); }}
                    onPress={(e) => { e?.stopPropagation?.(); onRemove(); }}
                    style={styles.removeButton}
                >
                    <Unicon name="close" size={13} colorVariant={colorVariant} />
                </Pressable>
            )}
        </>
    );

    if (onPress) {
        return (
            <Pressable
                testID={testID}
                accessibilityRole="button"
                accessibilityState={selected !== undefined ? { selected } : undefined}
                // stopPropagation so a chip nested inside another Pressable (e.g. a post
                // body) doesn't also trigger the parent's onPress navigation.
                onPointerDown={(e) => { e?.stopPropagation?.(); e?.preventDefault?.(); }}
                onPress={(e) => { e?.stopPropagation?.(); onPress(); }}
                style={({ pressed }) => [styles.chip(colorVariant), style, styles.pressable, pressed && styles.pressed]}
            >
                {chipContent}
            </Pressable>
        );
    }

    return (
        <View testID={testID} style={[styles.chip(colorVariant), style]}>
            {chipContent}
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts }) => ({
    chip: (colorVariant) => ({
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        backgroundColor: colors[`${colorVariant}Container`],
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 8,
        flexShrink: 1,
    }),
    chipText: (colorVariant) => ({
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors[colorVariant],
    }),
    removeButton: {
        alignItems: 'center',
        justifyContent: 'center',
        _web: {
            cursor: 'pointer',
        },
    },
    pressable: {
        _web: {
            cursor: 'pointer',
            transition: 'transform 0.08s ease, opacity 0.08s ease',
        },
    },
    pressed: {
        transform: [{ scale: 0.95 }],
        opacity: 0.9,
    },
}));
